import { createContext, useContext, type ReactNode } from 'react';
import { Permission, type PermissionAtom } from '@kolectra/shared/permissions';
import type { SessionInfo } from '@kolectra/shared/schemas';

/**
 * The project-surface toggle for a teamless PROJECT user (see project-shell.tsx). Kept in
 * its own dependency-light module so both the overview page and the team AppShell can read
 * it without a circular import through the shell.
 */

export type Surface = 'overview' | 'work';

export interface ProjectSurfaceValue {
  surface: Surface;
  setSurface: (s: Surface) => void;
  /** The user can see the project-wide overview (can_view_project_reports). */
  hasOverview: boolean;
  /** The user has at least one team-app work surface (judge/design/team/dashboard). */
  hasWork: boolean;
}

const Ctx = createContext<ProjectSurfaceValue | null>(null);

export function ProjectSurfaceProvider({ value, children }: { value: ProjectSurfaceValue; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The project-surface toggle, or null when not inside a ProjectUserApp (real team members). */
export function useProjectSurface(): ProjectSurfaceValue | null {
  return useContext(Ctx);
}

/** Team-app surfaces a project worker can hold (mirrors the AppShell nav gates). */
export const PROJECT_WORK_PERMS: PermissionAtom[] = [
  Permission.CAN_JUDGE,
  Permission.CAN_DESIGN,
  Permission.CAN_IMPORT_CSV,
  Permission.CAN_MANAGE_TEAM,
  Permission.CAN_VIEW_DASHBOARD,
  // The HLE console lives in the team router and is gated on this permission, so a
  // project admin who holds ONLY it still needs the work surface to reach it.
  Permission.CAN_MANAGE_PROJECT,
];

/** Whether a teamless project user has ANY web surface (else they belong on the no-access screen). */
export function projectHasWebSurface(session: SessionInfo): boolean {
  const perms = session.project?.permissions ?? [];
  return perms.includes(Permission.CAN_VIEW_PROJECT_REPORTS) || PROJECT_WORK_PERMS.some((p) => perms.includes(p));
}
